// We want these to match the API schema as closely as possible

import type { JSONSchema7 } from 'json-schema';

import type { DeepReadonly } from './types';

export const cohereJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    stream: {
      type: 'boolean',
      description: 'Whether to stream the response.',
    },
    tools: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            description: 'The name of the tool.',
          },
          description: {
            type: 'string',
            description: 'A description of the tool.',
          },
        },
        required: ['name'],
      },
      description: 'A list of available tools that the model may suggest invoking.',
    },
    documents: {
      type: 'array',
      description: 'A list of relevant documents the model can cite to generate a more accurate reply.',
      items: {
        oneOf: [
          {
            type: 'string',
            description: 'The document content as a string.',
          },
          {
            type: 'object',
            properties: {
              content: {
                type: 'string',
                description: 'The document content.',
              },
              metadata: {
                type: 'object',
                description: 'Metadata associated with the document.',
              },
            },
            required: ['content'],
          },
        ],
      },
    },
    citation_options: {
      type: 'object',
      description: 'Options for controlling citation generation.',
    },
    response_format: {
      type: 'object',
      description: 'Configuration for forcing the model output to adhere to a specified format.',
      properties: {
        type: {
          type: 'string',
          enum: ['json_object'],
          description: 'The type of format to enforce.',
        },
        json_schema: {
          type: 'object',
          description: 'A JSON schema to enforce a specific output structure.',
        },
      },
    },
    safety_mode: {
      type: 'string',
      enum: ['CONTEXTUAL', 'STRICT', 'OFF'],
      description: "Safety mode for the model's response.",
    },
    max_tokens: {
      type: 'integer',
      description: 'The maximum number of tokens the model will generate as part of the response.',
      minimum: 1,
    },
    stop_sequences: {
      type: 'array',
      items: {
        type: 'string',
      },
      description: 'A list of stop sequences to halt generation.',
    },
    temperature: {
      type: 'number',
      description: 'A non-negative float that tunes the degree of randomness in generation.',
      minimum: 0.0,
      maximum: 1.0,
      default: 0.3,
    },
    seed: {
      type: 'integer',
      description: 'A seed for deterministic sampling, ensuring the same result with identical parameters.',
      minimum: 0,
    },
    frequency_penalty: {
      type: 'number',
      description: 'Penalizes repetitiveness of generated tokens.',
      minimum: 0.0,
      maximum: 1.0,
      default: 0.0,
    },
    presence_penalty: {
      type: 'number',
      description: 'Penalizes repetition of any generated tokens.',
      minimum: 0.0,
      maximum: 1.0,
      default: 0.0,
    },
    k: {
      type: 'number',
      description: 'Ensures that only the top k most likely tokens are considered for generation at each step.',
      minimum: 0,
      maximum: 500,
      default: 0,
    },
    p: {
      type: 'number',
      description:
        'Ensures that only the most likely tokens, with total probability mass of p, are considered for generation.',
      minimum: 0.01,
      maximum: 0.99,
      default: 0.75,
    },
    logprobs: {
      type: 'boolean',
      description: 'When set to true, log probabilities of the generated tokens will be included in the response.',
    },
  },
  required: ['model', 'messages', 'stream'],
} as const;

// this confirms that the type is a valid JSON schema, but we want it as a `const` so we can use it to index the properties
export const cohereSchemaProperties: DeepReadonly<JSONSchema7> = cohereJsonSchema;

export const retrieveCohereParameters = (): string[] => Object.keys(cohereJsonSchema.properties);
